import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { RecordStatusObservation, useDocumentStatusStore } from '@/context/house/documents/RecordStatusContext';
import { useIsEnrichmentSettling } from '@/context/house/documents/EnrichmentSettlingContext';
import {
	getDocumentReadinessThresholds,
	hasPermanentFailure,
	isDocumentProcessingComplete,
	isEnrichmentPathInFlight,
	isKbPathInFlight,
} from '@/utils/pipelineStatusUtils';

export const DocumentReadinessState = {
	Preparing: 'preparing',
	Delayed: 'delayed',
	Ready: 'ready',
	Failed: 'failed',
	Stalled: 'stalled',
} as const;

export type DocumentReadinessState = (typeof DocumentReadinessState)[keyof typeof DocumentReadinessState];

/**
 * Per-document pipeline status flags derived from the house-level status stream.
 *
 * The V2 pipeline is a DAG with a fork — see ./PIPELINE_STATUS.md for the full map:
 *
 *     bnd_v2 → metadata_profiling → metadata_extraction → kbs
 *                                 ↘ document_enrichment
 *
 * The two user-visible resources have DIFFERENT owners, so they get their own flags:
 * - searchable in chat  ⟸ kbs                 (KB path: bnd_v2 → profiling → extraction → kbs)
 * - tags + summary      ⟸ document_enrichment (tag/summary path: bnd_v2 → profiling → document_enrichment)
 */
export type DocumentStatus = {
	/** Whether the house status endpoint has produced a successful response. */
	observation: RecordStatusObservation;
	/** Aggregate terminal state for the document's KB and enrichment branches. */
	readinessState: DocumentReadinessState | null;
	/**
	 * KB-indexing path still running — the document is not yet searchable in chat.
	 * Covers bnd_v2 → metadata_profiling → metadata_extraction → kbs.
	 */
	isUpdatingKnowledgeBase: boolean;
	/** document_enrichment itself is running — its output (classification tag + summary) is not applied yet. */
	isEnrichingDocuments: boolean;
	/**
	 * Tags/summary not yet available: the document is scheduled, or the tag/summary path
	 * (bnd_v2 → metadata_profiling → document_enrichment) is still in flight. metadata_extraction and
	 * kbs are deliberately excluded — they don't produce the tag or summary. Gate the Summary/Tags
	 * cards on this, not on isProcessingPipeline.
	 */
	isEnrichmentPending: boolean;
	/** True if ANY V2 pipeline node is still running (metadata_profiling included). Generic "still working". */
	isProcessingPipeline: boolean;
	/** Scheduled but not yet present in the status response (just created / not yet polled). */
	isPipelineScheduled: boolean;
};

/**
 * Returns pipeline status flags for a single document by id.
 * Reads houseId from HouseContext so callers only need to supply the document id.
 * Looks up the document in the house-level /status response so no extra request is made.
 */
export function useDocumentStatus(documentId: number | null | undefined, uploadedAt?: string | null): DocumentStatus {
	const statusStore = useDocumentStatusStore();
	const subscribe = useCallback(
		(onChange: () => void) => statusStore.subscribe(documentId, onChange),
		[documentId, statusStore],
	);
	const getSnapshot = useCallback(() => statusStore.getSnapshot(documentId), [documentId, statusStore]);
	const doc = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	const observation = documentId == null ? RecordStatusObservation.Unobserved : statusStore.observation;
	const [, forceDelayCheck] = useState(0);

	// Enrichment has completed in the status stream but the refreshed tags/summary haven't landed yet.
	// Fold into isEnrichmentPending so the Summary/Tags cards and the row's tags badge hold their
	// "Processing" placeholder until the data arrives, instead of clearing on the poll and flashing a gap.
	const isSettling = useIsEnrichmentSettling(documentId);

	// Not found in the status response means scheduled but not started yet. The endpoint returns every
	// non-deleted document, so finished docs stay present — an undefined doc means "just created / not
	// yet in the latest poll", not "finished".
	const isObserved = observation === RecordStatusObservation.Observed;
	const isPipelineScheduled = documentId != null && isObserved && doc === undefined;
	const processingFailed = hasPermanentFailure(doc);
	const processingComplete = isDocumentProcessingComplete(doc);
	const now = Date.now();
	const { delayedAt, stalledAt } =
		isObserved && !processingFailed && !processingComplete
			? getDocumentReadinessThresholds(doc, uploadedAt, now)
			: {};
	const isDelayed = delayedAt !== undefined && now >= delayedAt;
	const isStalled = stalledAt !== undefined && now >= stalledAt;
	const readinessState =
		documentId == null || !isObserved
			? null
			: processingFailed
				? DocumentReadinessState.Failed
				: processingComplete
					? DocumentReadinessState.Ready
					: isStalled
						? DocumentReadinessState.Stalled
						: isDelayed
							? DocumentReadinessState.Delayed
							: DocumentReadinessState.Preparing;
	let nextReadinessCheckAt: number | undefined;
	if (delayedAt !== undefined && delayedAt > now) nextReadinessCheckAt = delayedAt;
	if (stalledAt !== undefined && stalledAt > now) {
		nextReadinessCheckAt =
			nextReadinessCheckAt === undefined ? stalledAt : Math.min(nextReadinessCheckAt, stalledAt);
	}

	useEffect(() => {
		if (nextReadinessCheckAt === undefined) return;
		const timeout = window.setTimeout(
			() => forceDelayCheck((version) => version + 1),
			Math.max(nextReadinessCheckAt - Date.now(), 0) + 50,
		);
		return () => window.clearTimeout(timeout);
	}, [nextReadinessCheckAt]);

	// KB-readiness path (bnd_v2 → profiling → extraction → kbs). Terminal-based — in flight until kbs
	// COMPLETES (or the path fails) — so the handoff gaps between stages (where the next run is still
	// null) don't read as "not processing" and flicker the badge / KB indicator.
	const isUpdatingKnowledgeBase = isKbPathInFlight(doc);

	// Tag/summary path (forks after profiling) — independent of extraction/kbs.
	const isEnrichingDocuments = isEnrichmentPathInFlight(doc);

	const isEnrichmentPending = isPipelineScheduled || isEnrichmentPathInFlight(doc) || isSettling;

	// Any path still in flight — KB or enrichment.
	const isProcessingPipeline = isUpdatingKnowledgeBase || isEnrichingDocuments;

	return {
		observation,
		readinessState,
		isUpdatingKnowledgeBase,
		isEnrichingDocuments,
		isEnrichmentPending,
		isProcessingPipeline,
		isPipelineScheduled,
	};
}
